
import { Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/auth";
import Layout from "@/components/Layout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ProfileForm } from "@/components/profile/ProfileForm";
import { SecuritySettings } from "@/components/profile/SecuritySettings";
import { Skeleton } from "@/components/ui/skeleton";
import ReferralCard from "@/components/profile/ReferralCard";
import { useUserProfile } from "@/contexts/profile/ProfileContext";
import DeleteAccountSection from "@/components/profile/DeleteAccountSection";
import { useTranslation } from "react-i18next";
import SubscriptionTab from "@/components/subscription/SubscriptionTab";
import PageHero from "@/components/layout/PageHero";

const profileTabs = ['profile', 'security', 'subscription'] as const;
type ProfileTab = typeof profileTabs[number];

const isProfileTab = (value: string | null): value is ProfileTab =>
  profileTabs.includes(value as ProfileTab);

const ProfilePage = () => {
  const { user, loading: authLoading } = useAuth();
  const { profile, isLoading: isLoadingProfile } = useUserProfile();
  const [searchParams, setSearchParams] = useSearchParams();
  const isCompletionMode = searchParams.get('complete') === 'true';
  const requestedTab = searchParams.get('tab');
  const activeTab: ProfileTab = isProfileTab(requestedTab) ? requestedTab : 'profile';
  const { t } = useTranslation('common');

  const handleTabChange = (value: string) => {
    if (!isProfileTab(value)) {
      return;
    }

    const nextSearchParams = new URLSearchParams(searchParams);

    if (value === 'profile') {
      nextSearchParams.delete('tab');
    } else {
      nextSearchParams.set('tab', value);
    }

    setSearchParams(nextSearchParams, { replace: true });
  };

  if (authLoading) {
    return (
      <Layout>
        <LoadingProfileSkeleton />
      </Layout>
    );
  }

  if (!authLoading && !user) {
    return <Navigate to="/auth" replace />;
  }

  if (isLoadingProfile) {
    return (
      <Layout>
        <LoadingProfileSkeleton />
      </Layout>
    );
  }

  if (!profile) {
    return (
      <Layout>
        <div className="flex min-h-[50vh] items-center justify-center bg-brand-mist px-4 py-10 md:py-20">
          <div className="text-center">
            <p>{t("profile.profileNotFound")}</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="bg-brand-mist">
        <PageHero
          title={isCompletionMode ? t("profile.completeTitle") : t("profile.title")}
          description={isCompletionMode ? t("profile.completeDescription") : t("profile.description")}
          contentClassName="py-12 md:py-16"
        />

        <div className="container py-10 md:py-14">
          <div className="mx-auto max-w-4xl">
          {isCompletionMode ? (
            <ProfileForm profileData={profile} />
          ) : (
            <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
              <TabsList className="mb-6 grid h-auto w-full grid-cols-3 rounded-md border border-brand-cloud bg-white p-1 shadow-sm">
                <TabsTrigger value="profile">{t("profile.tabs.profile")}</TabsTrigger>
                <TabsTrigger value="security">{t("profile.tabs.security")}</TabsTrigger>
                <TabsTrigger value="subscription">Subscription</TabsTrigger>
              </TabsList>

              <TabsContent value="profile" className="mt-0">
                <ProfileForm profileData={profile} />
                <div className="mt-6">
                  <ReferralCard />
                </div>
              </TabsContent>

              <TabsContent value="security" className="mt-0">
                <SecuritySettings profileData={profile} />
                <DeleteAccountSection />
              </TabsContent>

              <TabsContent value="subscription" className="mt-0">
                <SubscriptionTab />
              </TabsContent>
            </Tabs>
          )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

const LoadingProfileSkeleton = () => {
  const { t } = useTranslation('common');
  
  return (
    <div className="bg-brand-mist">
      <PageHero
        title={t("profile.loading")}
        description={t("buttons.loading")}
        contentClassName="py-12 md:py-16"
      />
      <div className="container py-10 md:py-14">
      <div className="mx-auto max-w-4xl">
        <Skeleton className="h-10 w-full mb-6" />
        
        <div className="space-y-6">
          <Skeleton className="h-[400px] w-full rounded-md" />
        </div>
      </div>
      </div>
    </div>
  );
};

export default ProfilePage;
